import Markdown from 'components/commercetools-ui/organisms/markdown';
import { useFormat } from 'helpers/hooks/useFormat';
import { isStringHtml } from 'helpers/utils/isStringHtml';
import { useParams } from 'next/navigation';
import React, { useEffect, useState, type JSX } from 'react';
import './style.css';

interface SfmcPreference {
  preferenceId: string;
  status: string;
  optInQualifier: string;
  optInPromotion: string;
}

interface SfmcFormPayload {
  recipient: {
    [key: string]: any;
  };
  prefs?: SfmcPreference[];
}
export interface SfmcNewsletterFormProps {
  prefs: SfmcPreference[] | object;
  title: string;
  titleHtag?: string;
  body: string;
  submitButtonText: string;
  legalDisclaimer: string;
  confirmationMessage: string;
  details: object;
  dataAnalytics?: string;
  bgColor?: string;
  sectionId?: string;
}

const SfmcNewsletterForm: React.FC<SfmcNewsletterFormProps> = ({
  prefs,
  title,
  titleHtag,
  body,
  submitButtonText,
  legalDisclaimer,
  confirmationMessage,
  details,
  dataAnalytics,
  bgColor = 'padi-gray-light',
  sectionId,
}) => {
  const { locale } = useParams();
  const [formData, setFormData] = useState<SfmcFormPayload>();

  useEffect(() => {
    // Build preference objects.
    const preferences: SfmcPreference[] = [];
    Object.entries(prefs).forEach(([, value]) => {
      const preference = {
        preferenceId: value.prefId,
        status: value.prefStatus,
        optInQualifier: value.optinQualifier,
        optInPromotion: value.optinPromotion,
        optOutReason: value.optOutReason,
      };
      preferences.push(preference);
      setFormData({ recipient: {}, prefs: preferences });
    });
  }, [prefs]);

  const [formSuccess, setFormSuccess] = useState(false);
  const [formSuccessMessage, setFormSuccessMessage] = useState('');

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = e.target.name;
    const fieldValue = e.target.value;
    const localeSplit = locale.split('-');
    const language = localeSplit[0];
    setFormData((prevState) => ({
      ...prevState,
      recipient: {
        [fieldName]: fieldValue,
        communication_language_name: language,
        communication_language_name_overwrite: true,
      },
    }));
  };

  const submitForm = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formURL = form.action;
    const formMethod = form.method;

    fetch(formURL, {
      method: formMethod,
      body: JSON.stringify(formData),
      headers: {
        accept: 'application/json',
      },
    }).then(() => {
      setFormData(formData);
      setFormSuccess(true);
      setFormSuccessMessage(confirmationMessage);
    });
  };

  const { formatMessage: localizedForms } = useFormat({ name: 'forms' });

  const emailLabel = localizedForms({
    id: 'email.label',
    defaultMessage: 'Enter address',
  });

  const emailPlaceholder = localizedForms({
    id: 'email.placeholder',
    defaultMessage: 'Enter your email',
  });

  const TitleHtag = titleHtag ? (titleHtag as keyof JSX.IntrinsicElements) : 'h2';

  const formClassName = `sfmc relative isolate overflow-hidden bg-${bgColor} py-32 sm:py-96 lg:py-128`;

  return (
    <div data-id="sfmc-newsletter-container" className={formClassName} id={sectionId}>
      <div data-id="sfmc-newsletter-wrapper" className="mx-auto max-w-7xl px-24 lg:px-32">
        <div
          data-id="sfmc-newsletter-grid"
          className="mx-auto grid max-w-2xl grid-cols-1 gap-x-32 gap-y-64 lg:max-w-none lg:grid-cols-2"
        >
          {formSuccess ? (
            <div data-id="sfmc-newsletter-success" className="max-w-xl text-padi-gray-darkest lg:max-w-lg">
              <Markdown markdown={formSuccessMessage} />
            </div>
          ) : (
            <div data-id="sfmc-newsletter-form-section" className="max-w-xl lg:max-w-lg">
              <TitleHtag
                data-id="sfmc-newsletter-title"
                className="text-3xl font-bold tracking-tight text-padi-gray-darkest"
              >
                {title}
              </TitleHtag>
              <div data-id="sfmc-newsletter-body" className="prose mt-16 text-lg leading-8 text-padi-gray-darkest">
                {isStringHtml(body) ? <div dangerouslySetInnerHTML={{ __html: body }} /> : <Markdown markdown={body} />}
              </div>

              <form
                data-id="sfmc-newsletter-form"
                method="POST"
                action="https://prefapi.scubadiving.com/recipient"
                onSubmit={submitForm}
              >
                <div data-id="sfmc-newsletter-input-wrapper" className="mt-24 flex max-w-md gap-x-16">
                  <label data-id="sfmc-newsletter-email-label" htmlFor="email-address" className="sr-only">
                    {emailLabel}
                  </label>
                  <input
                    data-id="sfmc-newsletter-email-input"
                    id="email-address"
                    name="email_address"
                    type="email"
                    autoComplete="email"
                    required
                    className="min-w-0 flex-auto rounded-md border-0 bg-white px-14 py-8 text-padi-gray-darkest shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-padi-blue sm:text-sm sm:leading-6"
                    placeholder={emailPlaceholder}
                    onChange={handleInput}
                  />
                  <button
                    data-id="sfmc-newsletter-submit-button"
                    type="submit"
                    className="flex-none rounded-md bg-padi-blue px-14 py-10 text-sm font-semibold text-white shadow-sm hover:bg-padi-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-padi-blue"
                    padi-analytics={dataAnalytics}
                  >
                    {submitButtonText}
                  </button>
                </div>
              </form>
              <div data-id="sfmc-newsletter-legal-disclaimer" className="prose mt-16 text-sm text-padi-gray-darkest">
                {isStringHtml(legalDisclaimer) ? (
                  <div dangerouslySetInnerHTML={{ __html: legalDisclaimer }} />
                ) : (
                  <Markdown markdown={legalDisclaimer} />
                )}
              </div>
            </div>
          )}

          <dl
            data-id="sfmc-newsletter-details-list"
            className="grid grid-cols-1 gap-x-32 gap-y-40 sm:grid-cols-2 lg:pt-8"
          >
            {Object.values(details).map((detail, index) => (
              <div key={index} data-id={`sfmc-newsletter-detail-${index}`} className="flex flex-col items-start">
                <div
                  data-id={`sfmc-newsletter-detail-icon-wrapper-${index}`}
                  className="rounded-md bg-white/5 p-8 ring-1 ring-white/10"
                >
                  <span
                    data-id={`sfmc-newsletter-detail-icon-${index}`}
                    className="material-symbols-outlined text-4xl text-padi-gray-darkest"
                    aria-hidden="true"
                  >
                    {detail.icon}
                  </span>
                </div>
                <dt
                  data-id={`sfmc-newsletter-detail-title-${index}`}
                  className="mt-16 font-semibold text-padi-gray-darkest"
                >
                  {detail.title}
                </dt>
                <dd
                  data-id={`sfmc-newsletter-detail-body-${index}`}
                  className="prose mt-8 leading-7 text-padi-gray-darkest"
                >
                  {isStringHtml(detail.body) ? (
                    <div dangerouslySetInnerHTML={{ __html: 'test' }} />
                  ) : (
                    <Markdown markdown={detail.body} />
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
};

export default SfmcNewsletterForm;
